/**
 * Global dayjs plugin registration.
 * Import this module once at the app entry point (main.tsx) before any
 * component that calls .fromNow(), .local(), or locale-specific formatting.
 */
import 'dayjs/locale/de';
import 'dayjs/locale/en-gb';

import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);
dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);
dayjs.extend(localeData);

// Keep in sync with the dayjs/locale/* imports above and with eLanguage in
// src/context/localization. Adding a new app language requires (a) a new
// dayjs/locale import, (b) the lowercase BCP-47 tag here.
const SUPPORTED_DAYJS_LOCALES = new Set(['de', 'en-gb']);

/**
 * Map a BCP-47 locale tag from react-intl ('de-DE', 'en-GB') to a locale name
 * dayjs has registered. dayjs ships locales as lowercase BCP-47 ('en-gb') or
 * language-only ('de'); calling .locale('de-DE') silently falls back to English.
 */
export const dayjsLocale = (locale: string): string => {
  const lower = locale.toLowerCase();
  if (SUPPORTED_DAYJS_LOCALES.has(lower)) return lower;
  return lower.split('-')[0];
};
